import React, { useState, useEffect } from 'react';
import {
    Box,
    VStack,
    HStack,
    Heading,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    Card,
    CardBody,
    CardHeader,
    Badge,
    IconButton,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    FormControl,
    FormLabel,
    Textarea,
    Spinner,
    Alert,
    AlertIcon,
    useColorModeValue,
    Checkbox,
    Flex,
    Text,
    Tooltip,
    Switch,
    Code,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    SimpleGrid,
    Divider,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    useToast,
    ButtonGroup,
    FormErrorMessage,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverArrow,
    PopoverCloseButton,
    PopoverHeader,
    PopoverBody
} from '@chakra-ui/react';
import {
    MdSearch,
    MdAdd,
    MdEdit,
    MdDelete,
    MdMoreVert,
    MdRefresh,
    MdCode,
    MdDownload,
    MdUpload,
    MdSchedule,
    MdToggleOn,
    MdToggleOff,
    MdPlayArrow,
    MdBarChart,
    MdVisibility,
    MdFileDownload,
    MdFileUpload,
    MdInfoOutline
} from 'react-icons/md';
import * as XLSX from 'xlsx';
import apiService from '../services/apiService';
import { formatDateTime, formatNumber, toTitleCase } from '../utils/helpers';
import { ToastManager } from '../components/Common/ToastManager';
// Enhanced components with auto-sizing support
import SQLHighlighter from '../components/Common/SQLHighlighter';
import JSONFormatter from '../components/Common/JSONFormatter';

// Common cron expressions (6-field Quartz: second minute hour day month dayOfWeek, UTC).
// Shown in the help popover next to the Cron Expression field; click to apply.
const CRON_EXAMPLES = [
    { label: 'Every 15 minutes', value: '0 */15 * ? * *' },
    { label: 'Every 30 minutes', value: '0 */30 * ? * *' },
    { label: 'Every hour',       value: '0 0 * ? * *' },
    { label: 'Every 3 hours',    value: '0 0 */3 ? * *' },
    { label: 'Every day at 8 AM',  value: '0 0 8 ? * *' },
    { label: 'Every day at 12 PM', value: '0 0 12 ? * *' },
];

/**
 * Reports Management Page Component with Auto-Highlighting and Auto-Sizing
 */
function Report({ userProfile, appConfig, authServiceReady }) {
    // State management for reports data and UI interactions
    const [reports, setReports] = useState([]);
    const [reportModules, setReportModules] = useState([]); // module master for the dropdown
    const [filteredReports, setFilteredReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exportLoading, setExportLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedReports, setSelectedReports] = useState([]);
    const [currentReport, setCurrentReport] = useState(null);
    const [statistics, setStatistics] = useState(null);
    
    // Enhanced pagination state matching backend PageResponse structure
    const [pagination, setPagination] = useState({
        page: 1,
        size: 20,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false
    });

    // Modal disclosure states for different operations
    const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
    const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
    const { isOpen: isImportOpen, onOpen: onImportOpen, onClose: onImportClose } = useDisclosure();
    const { isOpen: isStatsOpen, onOpen: onStatsOpen, onClose: onStatsClose } = useDisclosure();
    const { isOpen: isBatchDeleteOpen, onOpen: onBatchDeleteOpen, onClose: onBatchDeleteClose } = useDisclosure();
    const { isOpen: isModuleOpen, onOpen: onModuleOpen, onClose: onModuleClose } = useDisclosure();
    const [newModuleName, setNewModuleName] = useState('');
    const [moduleSaving, setModuleSaving] = useState(false);

    // Enhanced form state matching ReportRequest.java structure with comprehensive validation
    const [formData, setFormData] = useState({
        report: '',
        reportDescription: '',
        alertMessage: '', 
        parameters: [],
        parametersJson: '[]',
        isEnabled: true,
        cronExpression: '',
        isCronEnabled: false,
        isTeamsAlertEnabled: true,
        reportType:'info',
        isAutoCloseIssues: false,
        reportModule: '',
        dataQuery: '',
        alertRepeatEnabled: false
    });

    // Form validation errors
    const [formErrors, setFormErrors] = useState({});

    // Import/export state for data transfer operations
    const [importData, setImportData] = useState('');
    const [exportData, setExportData] = useState('');

    // Theme colors for consistent styling across light and dark modes
    const cardBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const textColor = useColorModeValue('gray.600', 'gray.300');
    const headingColor = useColorModeValue('gray.800', 'white');
    const codeBg = useColorModeValue('gray.50', 'gray.900');
    const bgColor = useColorModeValue('gray.50', 'gray.700');

    const toast = useToast();

    /**
     * Initialize component and load report data on authentication ready
     * Sets up initial state and triggers comprehensive data fetching
     */
    useEffect(() => {
        if (authServiceReady) {
            loadStatistics();
            loadReportModules();
        }
    }, [authServiceReady]);

    /**
     * Load the report module master list (CAS_REPORT_MODULE) for the module dropdown.
     */
    const loadReportModules = async () => {
        try {
            const res = await apiService.getReportModules();
            if (res.success && Array.isArray(res.data)) {
                setReportModules(res.data);
            }
        } catch (error) {
            // Non-blocking: the dropdown will simply be empty if this fails
            console.error('Failed to load report modules', error);
        }
    };

    /**
     * Create a new report module (master). On success, refresh the list and select it.
     */
    const handleCreateModule = async () => {
        const name = newModuleName.trim();
        if (!name) {
            ToastManager.showError('Module name is required');
            return;
        }
        try {
            setModuleSaving(true);
            const res = await apiService.createReportModule({ name });
            if (res.success) {
                ToastManager.showSuccess('Module created successfully');
                await loadReportModules();
                // Select the newly created module in the form (value defaults to name)
                const createdValue = res.data?.value || name;
                setFormData(prev => ({ ...prev, reportModule: createdValue }));
                setNewModuleName('');
                onModuleClose();
            } else {
                ToastManager.showError(res.message || 'Failed to create module');
            }
        } catch (error) {
            ToastManager.showError(`Failed to create module: ${error.message}`);
        } finally {
            setModuleSaving(false);
        }
    };

    /**
     * Load reports when pagination, search, or filters change
     * Properly triggers data loading on any pagination parameter change
     */
    useEffect(() => {
        if (authServiceReady) {
            loadReports();
        }
    }, [authServiceReady, pagination.page, pagination.size, searchTerm, statusFilter]);

    /**
     * Filter reports based on search and status criteria with real-time updates
     * Applies client-side filtering for immediate user feedback
     */
    useEffect(() => {
        filterReports();
    }, [reports, searchTerm, statusFilter]);

    /**
     * Load reports from backend API with comprehensive pagination support
     * Fetches paginated report data matching ReportController.getReports implementation
     * Includes proper error handling and loading state management
     */
    const loadReports = async () => {
        try {
            setLoading(true);

            const params = {
                page: pagination.page,
                size: pagination.size,
                sortBy: 'reportCode',
                sortDirection: 'DESC'
            };

            // Add search if present
            if (searchTerm && searchTerm.trim()) {
                params.search = searchTerm.trim();
            }

            const response = await apiService.getReports(params);
            if (response.success) {
                // Handle exact backend response structure
                const responseData = response.data;
                const reportsData = responseData.content || [];
                
                // Map to ensure all fields are properly handled
                const mappedReports = reportsData.map(report => ({
                    ...report,
                    parameterCount: report.parameters ? report.parameters.length : 0
                }));

                setReports(mappedReports);

                // Handle pagination from backend PageResponse properly
                if (responseData.pagination) {
                    setPagination(prev => ({
                        ...prev,
                        total: responseData.pagination.total || 0,
                        totalPages: responseData.pagination.totalPages || 0,
                        hasNext: responseData.pagination.hasNext || false,
                        hasPrevious: responseData.pagination.hasPrevious || false
                    }));
                } else {
                    // Fallback for non-paginated response
                    setPagination(prev => ({
                        ...prev,
                        total: mappedReports.length,
                        totalPages: 1,
                        hasNext: false,
                        hasPrevious: false
                    }));
                }
            } else {
                throw new Error(response.error?.message || 'Failed to load reports');
            }
        } catch (error) {
            ToastManager.showError(`Failed to load reports: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Load all reports data for Excel export
     * Fetches complete dataset across all pages with current search/filter criteria
     */
    const loadAllReportsForExport = async () => {
        try {
            // Build request parameters to get all data
            const requestParams = {
                page: 1,
                size: pagination.total > 0 ? pagination.total : 10000, // Use total count or large number
                sortBy: 'reportCode',
                sortDirection: 'DESC'
            };

            // Add search if present to maintain filter consistency
            if (searchTerm && searchTerm.trim()) {
                requestParams.search = searchTerm.trim();
            }

            const response = await apiService.getReports(requestParams);
            if (response.success) {
                const responseData = response.data;
                const allReportsData = responseData.content || responseData || [];
                
                // Map to ensure all fields are properly handled
                return allReportsData.map(report => ({
                    ...report,
                    parameterCount: report.parameters ? report.parameters.length : 0
                }));
            }
            throw new Error(response.error?.message || 'Failed to load all reports');
        } catch (error) {
            throw new Error(`Failed to fetch complete report data: ${error.message}`);
        }
    };

    /**
     * Load comprehensive report statistics for dashboard display
     * Fetches statistical data from ReportController.getReportStatistics
     * Provides insights into report usage, status distribution, and performance metrics
     */
    const loadStatistics = async () => {
        try {
            const response = await apiService.getReportStatistics();
            if (response.success) {
                setStatistics(response.data);
            }
        } catch (error) {
            // Statistics failure is not critical for main functionality
        }
    };

    /**
     * Filter reports based on search term and status with advanced criteria
     * Implements client-side filtering for immediate feedback and enhanced UX
     * Supports multiple filter combinations and partial text matching
     */
    const filterReports = () => {
        let filtered = [...reports];

        // Apply comprehensive search filter on multiple fields (only if no server-side search)
        if (searchTerm.trim() && !searchTerm) { // This ensures client-side filtering only when needed
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(report =>
                report.reportDescription?.toLowerCase().includes(searchLower) ||
                report.reportCode?.toString().includes(searchLower) ||
                report.report?.toLowerCase().includes(searchLower) ||
                report.alertMessage?.toLowerCase().includes(searchLower)
            );
        }

        // Apply advanced status filtering with multiple criteria (only if no server-side filtering)
        if (statusFilter !== 'all' && !searchTerm) {
            filtered = filtered.filter(report => {
                switch (statusFilter) {
                    case 'enabled':
                        return report.isEnabled;
                    case 'disabled':
                        return !report.isEnabled;
                    case 'scheduled':
                        return report.isCronEnabled && report.isEnabled;
                    case 'unscheduled':
                        return !report.isCronEnabled;
                    case 'schedulable':
                        return report.isSchedulable;
                    case 'with_parameters':
                        return report.parameterCount > 0;
                    case 'no_parameters':
                        return report.parameterCount === 0;
                    default:
                        return true;
                }
            });
        }

        setFilteredReports(filtered);
    };

    /**
     * Handle search term changes with debouncing
     * Resets pagination to page 1 when search changes
     */
    const handleSearchChange = (value) => {
        setSearchTerm(value);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    /**
     * Handle status filter changes
     * Resets pagination to page 1 when filter changes
     */
    const handleStatusFilterChange = (value) => {
        setStatusFilter(value);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    /**
     * Enhanced validate report form data with comprehensive security checks
     */
    const validateReportData = () => {
        const errors = {};

        // Validate required SQL query field with detailed checks
        if (!formData.report?.trim()) {
            errors.report = 'SQL query is required';
        } else if (formData.report.length > 10000) {
            errors.report = 'SQL query too long (max 10,000 characters)';
        }
        // } else {
        //     // Enhanced SQL injection protection with comprehensive pattern matching
        //     const dangerousStatements = /\b(insert|update|delete|drop|create|alter|truncate|exec|execute|sp_|xp_|openrowset|openquery)\b/i;
        //     if (dangerousStatements.test(formData.report)) {
        //         errors.report = 'SQL query contains prohibited DML/DDL statements, stored procedures, or bulk operations';
        //     } else {
        //         // // Validate SQL query structure and enforce SELECT-only operations
        //         // const queryUpper = formData.report.trim().toUpperCase();
        //         // if (!queryUpper.startsWith('SELECT')) {
        //         //     errors.report = 'Query must start with SELECT statement for read-only operations';
        //         // }
        //     }
        // }

        // Validate required description field
        if (!formData.reportDescription?.trim()) {
            errors.reportDescription = 'Report description is required';
        } else if (formData.reportDescription.length > 1000) {
            errors.reportDescription = 'Description too long (max 1,000 characters)';
        }

        // Validate required alert message field
        if (!formData.alertMessage?.trim()) {
            errors.alertMessage = 'Report alert message is required';
        } else if (formData.alertMessage.length > 2000) {
            errors.alertMessage = 'Alert message too long (max 2,000 characters)';
        }

        // Comprehensive JSON parameters validation
        try {
            const params = JSON.parse(formData.parametersJson);
            if (!Array.isArray(params)) {
                errors.parametersJson = 'Parameters must be a valid JSON array';
            } else if (params.length > 50) {
                errors.parametersJson = 'Maximum 50 parameters allowed per report';
            } else {
                // Enhanced parameter structure validation with detailed error messages
                for (let i = 0; i < params.length; i++) {
                    const param = params[i];
                    if (!param || typeof param !== 'object') {
                        errors.parametersJson = `Parameter ${i + 1} must be an object`;
                        break;
                    }

                    // Validate required parameter fields
                    const requiredFields = ['category', 'type', 'index'];
                    for (const field of requiredFields) {
                        if (!(field in param)) {
                            errors.parametersJson = `Parameter ${i + 1} missing required field: ${field}`;
                            break;
                        }
                    }

                    if (errors.parametersJson) break;

                    // Validate field types and value ranges
                    if (typeof param.index !== 'number' || param.index < 0 || param.index > 999) {
                        errors.parametersJson = `Parameter ${i + 1} index must be a number between 0-999`;
                        break;
                    }

                    if (typeof param.category !== 'string' || !param.category.trim()) {
                        errors.parametersJson = `Parameter ${i + 1} category must be a non-empty string`;
                        break;
                    }

                    if (typeof param.type !== 'string' || !param.type.trim()) {
                        errors.parametersJson = `Parameter ${i + 1} type must be a non-empty string`;
                        break;
                    }
                }

                // Update parameters array in form data for consistency
                if (!errors.parametersJson) {
                    setFormData(prev => ({ ...prev, parameters: params }));
                }
            }
        } catch (error) {
            errors.parametersJson = 'Parameters must be valid JSON: ' + error.message;
        }

        // Advanced cron expression validation for scheduling
        if (formData.isCronEnabled && !formData.cronExpression?.trim()) {
            errors.cronExpression = 'Cron expression required when scheduling is enabled';
        } else if (formData.isCronEnabled && formData.cronExpression?.trim()) {
            const cronParts = formData.cronExpression.trim().split(/\s+/);
            if (cronParts.length !== 6) {
                errors.cronExpression = 'Cron expression must have 6 parts: second minute hour day month dayOfWeek';
            }
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    /**
     * Handle report creation with enhanced validation and audit trail
     * Creates new report with comprehensive validation and proper error handling
     * Includes user context tracking and success feedback
     */
    const handleCreateReport = async () => {
        try {
            if (!validateReportData()) return;

            const reportData = {
                report: formData.report.trim(),
                reportDescription: formData.reportDescription.trim(),
                alertMessage: formData.alertMessage.trim(),
                parameters: formData.parameters,
                isEnabled: formData.isEnabled,
                cronExpression: formData.cronExpression?.trim() || null,
                isCronEnabled: formData.isCronEnabled,
                createdBy: userProfile?.email || 'system@emaildashboard.com',
                isTeamsAlertEnabled:formData.isTeamsAlertEnabled,
                reportType:formData.reportType.trim(),
                isAutoCloseIssues: formData.isAutoCloseIssues,
                reportModule:formData.reportModule.trim(),
                dataQuery:formData.dataQuery.trim(),
                alertRepeatEnabled:formData.alertRepeatEnabled
            };

            const response = await apiService.createReport(reportData);
            
            if (response.success) {
                ToastManager.showSuccess('Report created successfully with proper validation');
                onCreateClose();
                resetForm();
                loadReports();
                loadStatistics();
            } else {
                throw new Error(response.error?.message || 'Failed to create report');
            }
        } catch (error) {
            ToastManager.showError(`Failed to create report: ${error.message}`);
        }
    };

    /**
     * Handle report update with enhanced validation and change tracking
     * Updates existing report with comprehensive validation and audit trail
     * Maintains data integrity and provides detailed feedback
     */
    const handleUpdateReport = async () => {
        try {
            if (!currentReport?.reportCode) return;
            if (!validateReportData()) return;
            console.log(formData);
            const reportData = {
                report: formData.report.trim(),
                reportDescription: formData.reportDescription.trim(),
                alertMessage: formData.alertMessage.trim(), // NEW: Include alert message
                parameters: formData.parameters,
                isEnabled: formData.isEnabled,
                cronExpression: formData.cronExpression?.trim() || null,
                isCronEnabled: formData.isCronEnabled,
                updatedBy: userProfile?.email || 'system@emaildashboard.com',
                isTeamsAlertEnabled:formData.isTeamsAlertEnabled,
                reportType:formData.reportType.trim(),
                isAutoCloseIssues:formData.isAutoCloseIssues,
                reportModule:formData.reportModule.trim(),
                dataQuery:formData.dataQuery.trim(),
                alertRepeatEnabled:formData.alertRepeatEnabled
            };
            const response = await apiService.updateReport(currentReport.reportCode, reportData);
            
            if (response.success) {
                ToastManager.showSuccess('Report updated successfully with validation');
                onEditClose();
                resetForm();
                loadReports();
                loadStatistics();
            } else {
                throw new Error(response.error?.message || 'Failed to update report');
            }
        } catch (error) {
            ToastManager.showError(`Failed to update report: ${error.message}`);
        }
    };

    /**
     * Handle SQL query change with advanced syntax validation
     * Updates form data and provides immediate feedback on SQL syntax and security
     * Implements real-time validation for better user experience
     */
    const handleSQLChange = (value) => {
        setFormData(prev => ({ ...prev, report: value }));
        // Clear error when user starts typing
        if (formErrors.report) {
            setFormErrors(prev => ({ ...prev, report: undefined }));
        }
    };

    const  handleDataSQLChange = (value) =>{
        setFormData(prev =>({...prev, dataQuery: value }))
        if(formErrors.dataQuery){
            setFormErrors(prev => ({ ...prev, dataQuery: undefined }));
        }
    }
    

    /**
     * Handle JSON parameters change with comprehensive validation
     * Updates form data and validates parameter structure in real-time
     * Provides immediate feedback on JSON syntax and structure validity
     */
    const handleParametersChange = (value) => {
        setFormData(prev => ({ ...prev, parametersJson: value }));
        // Clear error when user starts typing
        if (formErrors.parametersJson) {
            setFormErrors(prev => ({ ...prev, parametersJson: undefined }));
        }
        
        // Attempt real-time JSON parsing and parameter validation
        try {
            if (value.trim()) {
                const parsed = JSON.parse(value);
                if (Array.isArray(parsed)) {
                    // Update parsed parameters for immediate use
                    setFormData(prev => ({ ...prev, parameters: parsed }));
                }
            }
        } catch (error) {
            // JSON syntax error - will be caught in full validation
            // Don't show immediate error to avoid disrupting typing
        }
    };

    /**
     * Handle alert message change
     * Updates form data and clears validation errors
     */
    const handleAlertMessageChange = (value) => {
        setFormData(prev => ({ ...prev, alertMessage: value }));
        // Clear error when user starts typing
        if (formErrors.alertMessage) {
            setFormErrors(prev => ({ ...prev, alertMessage: undefined }));
        }
    };

    /**
     * Handle import data change with comprehensive validation
     * Updates import data and provides immediate feedback on structure validity
     * Validates import JSON structure and provides user guidance
     */
    const handleImportDataChange = (value) => {
        setImportData(value);
        
        // Real-time import structure validation
        try {
            if (value.trim()) {
                const parsed = JSON.parse(value);
                
                // Validate expected import structure
                const requiredFields = ['report', 'reportDescription', 'alertMessage'];
                const missingFields = requiredFields.filter(field => !parsed[field]);
                
                if (missingFields.length > 0) {
                    // Structure validation feedback will be handled in import function
                }
                
                // Additional validation for optional fields
                if (parsed.parameters && !Array.isArray(parsed.parameters)) {
                    // Parameter structure validation
                }
            }
        } catch (error) {
            // Invalid JSON structure - will be caught in import validation
        }
    };

    /**
     * Handle report deletion with confirmation and audit trail
     * Safely deletes report with proper validation and user feedback
     * Maintains referential integrity through programmatic cascade handling
     */
    const handleDeleteReport = async () => {
        try {
            if (!currentReport?.reportCode) return;

            const response = await apiService.deleteReport(currentReport.reportCode);
            
            if (response.success) {
                ToastManager.showSuccess('Report deleted successfully');
                onDeleteClose();
                setCurrentReport(null);
                loadReports();
                loadStatistics();
            } else {
                throw new Error(response.error?.message || 'Failed to delete report');
            }
        } catch (error) {
            ToastManager.showError(`Failed to delete report: ${error.message}`);
        }
    };

    /**
     * Handle batch deletion with comprehensive validation and feedback
     * Efficiently deletes multiple reports with proper error handling
     * Provides detailed feedback on deletion results and any failures
     */
    const handleBatchDelete = async () => {
        try {
            if (selectedReports.length === 0) return;

            const response = await apiService.batchDeleteReports(selectedReports);
            
            if (response.success) {
                const deletedCount = response.data?.deletedCount || selectedReports.length;
                ToastManager.showSuccess(`${deletedCount} reports deleted successfully`);
                setSelectedReports([]);
                onBatchDeleteClose();
                loadReports();
                loadStatistics();
            } else {
                throw new Error(response.error?.message || 'Failed to delete reports');
            }
        } catch (error) {
            ToastManager.showError(`Failed to delete reports: ${error.message}`);
        }
    };

    /**
     * Handle report status toggle with immediate feedback
     * Toggles report enabled/disabled status with optimistic updates
     * Provides immediate user feedback and handles rollback on failure
     */
    const handleToggleStatus = async (reportCode, currentStatus) => {
        try {
            const newStatus = !currentStatus;
            const response = await apiService.toggleReportStatus(reportCode, newStatus);
            
            if (response.success) {
                ToastManager.showSuccess(`Report ${newStatus ? 'enabled' : 'disabled'} successfully`);
                loadReports();
                loadStatistics();
            } else {
                throw new Error(response.error?.message || 'Failed to toggle status');
            }
        } catch (error) {
            ToastManager.showError(`Failed to toggle status: ${error.message}`);
        }
    };

    /**
     * Handle scheduling toggle with validation and feedback
     * Toggles report scheduling status with proper validation
     * Ensures scheduling dependencies are met before enabling
     */
    const handleToggleScheduling = async (reportCode, currentStatus) => {
        try {
            const newStatus = !currentStatus;
            const response = await apiService.toggleReportScheduling(reportCode, newStatus);
            
            if (response.success) {
                ToastManager.showSuccess(`Report scheduling ${newStatus ? 'enabled' : 'disabled'} successfully`);
                loadReports();
                loadStatistics();
            } else {
                throw new Error(response.error?.message || 'Failed to toggle scheduling');
            }
        } catch (error) {
            ToastManager.showError(`Failed to toggle scheduling: ${error.message}`);
        }
    };

    /**
     * Handle report export with comprehensive data formatting
     * Exports report definition in standard JSON format
     * Includes all necessary metadata for successful import
     */
    const handleExportReport = async (reportCode) => {
        try {
            const response = await apiService.exportReportDefinition(reportCode);
            
            if (response.success) {
                setExportData(response.data);
                
                // Create downloadable file with proper naming convention
                const blob = new Blob([response.data], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `report_${reportCode}_export_${new Date().toISOString().split('T')[0]}.json`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                
                ToastManager.showSuccess('Report exported successfully');
            } else {
                throw new Error(response.error?.message || 'Failed to export report');
            }
        } catch (error) {
            ToastManager.showError(`Failed to export report: ${error.message}`);
        }
    };

    /**
     * Export ALL reports to Excel across all pages
     * Downloads complete report data in Excel format with proper formatting
     * Optimized to fetch all data before export regardless of current pagination
     */
    const handleExportReportsExcel = async () => {
        try {
            setExportLoading(true);

            // Step 1: Fetch all report data across all pages
            const allReportsData = await loadAllReportsForExport();

            if (!allReportsData || allReportsData.length === 0) {
                ToastManager.showError('No report data available for export');
                return;
            }

            // Step 2: Prepare data for Excel export with complete dataset including alert message
            const excelData = allReportsData.map(report => ({
                'Report Code': report.reportCode,
                'Description': report.reportDescription,
                'Alert Message': report.alertMessage || 'N/A', // NEW: Include alert message
                'Status': report.isEnabled ? 'Enabled' : 'Disabled',
                'Scheduling': report.isCronEnabled ? 'Enabled' : 'Disabled',
                'Cron Expression': report.cronExpression || 'N/A',
                'Parameter Count': report.parameterCount || 0,
                'Is Schedulable': report.isSchedulable ? 'Yes' : 'No',
                'Created Date': formatDateTime(report.createdAtUtc),
                'Created By': report.createdBy || 'N/A',
                'Updated Date': formatDateTime(report.updatedAtUtc),
                'Updated By': report.updatedBy || 'N/A',
                'SQL Query Length': report.report ? report.report.length : 0,
                'Has NOLOCK': report.report && (report.report.includes('NOLOCK') || report.report.includes('WITH(NOLOCK)')) ? 'Yes' : 'No'
            }));

            // Step 3: Create workbook and worksheet
            const workbook = XLSX.utils.book_new();
            const worksheet = XLSX.utils.json_to_sheet(excelData);

            // Step 4: Set column widths for better readability
            const columnWidths = [
                { wch: 15 }, // Report Code
                { wch: 40 }, // Description
                { wch: 30 }, // Alert Message
                { wch: 12 }, // Status
                { wch: 12 }, // Scheduling
                { wch: 20 }, // Cron Expression
                { wch: 15 }, // Parameter Count
                { wch: 15 }, // Is Schedulable
                { wch: 20 }, // Created Date
                { wch: 25 }, // Created By
                { wch: 20 }, // Updated Date
                { wch: 25 }, // Updated By
                { wch: 18 }, // SQL Query Length
                { wch: 12 }  // Has NOLOCK
            ];
            worksheet['!cols'] = columnWidths;

            // Step 5: Add worksheet to workbook
            XLSX.utils.book_append_sheet(workbook, worksheet, 'All Reports');

            // Step 6: Add comprehensive metadata worksheet
            const metadataSheet = XLSX.utils.json_to_sheet([
                { Property: 'Export Date', Value: new Date().toISOString() },
                { Property: 'Total Records Exported', Value: allReportsData.length },
                { Property: 'Current Page Records', Value: filteredReports.length },
                { Property: 'Total Database Records', Value: pagination.total },
                { Property: 'Search Term Applied', Value: searchTerm || 'None' },
                { Property: 'Status Filter Applied', Value: statusFilter === 'all' ? 'All Statuses' : statusFilter },
                { Property: 'Export Scope', Value: 'Complete Dataset (All Pages)' },
                { Property: 'Exported By', Value: userProfile?.email || 'System Administrator' },
                { Property: 'Application', Value: 'Report Management System' },
                { Property: 'System Version', Value: '1.0.0' }
            ]);
            metadataSheet['!cols'] = [{ wch: 25 }, { wch: 35 }];
            XLSX.utils.book_append_sheet(workbook, metadataSheet, 'Export Metadata');

            // Step 7: Generate filename with current date and scope indicator
            const filename = `reports_complete_export_${new Date().toISOString().split('T')[0]}.xlsx`;

            // Step 8: Write and download the file
            XLSX.writeFile(workbook, filename);

            ToastManager.showSuccess(
                `Complete report database exported successfully! 
                ${allReportsData.length} records exported to ${filename}`
            );

        } catch (error) {
            ToastManager.showError(`Failed to export complete report data: ${error.message}`);
        } finally {
            setExportLoading(false);
        }
    };

    /**
     * Handle report import with comprehensive validation
     * Imports report definition with full structure validation
     * Provides detailed feedback on import success or failure reasons
     */
    const handleImportReport = async () => {
        try {
            if (!importData.trim()) {
                ToastManager.showError('Import data is required');
                return;
            }

            // Validate JSON structure before sending to server
            try {
                const parsedData = JSON.parse(importData);
                
                // Validate required fields including alert message
                if (!parsedData.report || !parsedData.reportDescription || !parsedData.alertMessage) {
                    ToastManager.showError('Import data must include report, reportDescription, and alertMessage fields');
                    return;
                }
            } catch (error) {
                ToastManager.showError('Invalid JSON format: ' + error.message);
                return;
            }

            const response = await apiService.importReportDefinition(importData);
            
            if (response.success) {
                ToastManager.showSuccess('Report imported successfully with validation');
                onImportClose();
                setImportData('');
                loadReports();
                loadStatistics();
            } else {
                throw new Error(response.error?.message || 'Failed to import report');
            }
        } catch (error) {
            ToastManager.showError(`Failed to import report: ${error.message}`);
        }
    };

    /**
     * Open create modal with proper form reset
     * Ensures clean state when creating new reports
     * Clears any data that might be left from edit operations
     */
    const openCreateModal = () => {
        resetForm(); // Ensure clean form state
        onCreateOpen();
    };

    /**
     * Open edit modal with enhanced form population and auto-formatting
     * Prepares form for editing existing report with proper JSON formatting
     * Auto-formats JSON parameters for better readability and editing
     */
    const openEditModal = (report) => {
        setCurrentReport(report);
        setFormData({
            report: report.report || '',
            reportDescription: report.reportDescription || '',
            alertMessage: report.alertMessage || '', // NEW: Include alert message
            parameters: report.parameters || [],
            parametersJson: JSON.stringify(report.parameters || [], null, 2),
            isEnabled: report.isEnabled !== undefined ? report.isEnabled : true,
            cronExpression: report.cronExpression || '',
            isCronEnabled: report.isCronEnabled || false,
            isTeamsAlertEnabled: report.isTeamsAlertEnabled !==undefined ? report.isTeamsAlertEnabled: true,
            reportType:report.reportType || 'info',
            isAutoCloseIssues:report.isAutoCloseIssues || false,
            reportModule:report.reportModule || 'others',
            dataQuery:report.dataQuery||'',
            alertRepeatEnabled: report.alertRepeatEnabled || false
        });
        setFormErrors({}); // Clear any existing errors
        onEditOpen();
    };

    /**
     * Open view modal with current report context
     * Sets up read-only view with auto-highlighting enabled
     * Provides comprehensive report details display
     */
    const openViewModal = (report) => {
        setCurrentReport(report);
        onViewOpen();
    };

    /**
     * Open delete confirmation modal with report context
     * Sets up safe deletion workflow with confirmation
     */
    const openDeleteModal = (report) => {
        setCurrentReport(report);
        onDeleteOpen();
    };

    /**
     * Reset form data to initial state with proper JSON formatting
     * Clears all form fields and returns to clean state
     * Ensures consistent initialization for create operations
     */
    const resetForm = () => {
        setFormData({
            report: '',
            reportDescription: '',
            alertMessage: '', // NEW: Reset alert message
            parameters: [],
            parametersJson: '[]',
            isEnabled: true,
            cronExpression: '',
            isCronEnabled: false,
            isTeamsAlertEnabled:true,
            reportType: 'info',
            isAutoCloseIssues:false,
            reportModule: 'others',
            dataQuery:'',
            alertRepeatEnabled:false
        });
        setFormErrors({}); // Clear validation errors
        setCurrentReport(null);
    };

    /**
     * Handle individual report selection for batch operations
     * Manages selected reports state for bulk actions
     * Provides immediate feedback on selection changes
     */
    const handleReportSelection = (reportCode, isSelected) => {
        if (isSelected) {
            setSelectedReports(prev => [...prev, reportCode]);
        } else {
            setSelectedReports(prev => prev.filter(code => code !== reportCode));
        }
    };

    /**
     * Handle select all/none functionality for batch operations
     * Efficiently manages bulk selection state
     * Provides immediate visual feedback on selection changes
     */
    const handleSelectAll = (isSelected) => {
        if (isSelected) {
            setSelectedReports(filteredReports.map(report => report.reportCode));
        } else {
            setSelectedReports([]);
        }
    };

    /**
     * Handle pagination navigation with state management
     * Updates current page and triggers data reload automatically via useEffect
     */
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, page: newPage }));
        }
    };

    /**
     * Handle page size change
     * Updates size and resets to page 1
     */
    const handlePageSizeChange = (newSize) => {
        setPagination(prev => ({ 
            ...prev, 
            page: 1,  // Reset to first page
            size: parseInt(newSize, 10) 
        }));
    };

    /**
     * Get status color based on report state for consistent UI theming
     * Returns appropriate color scheme for different report statuses
     * Provides visual consistency across status indicators
     */
    const getStatusColor = (report) => {
        if (!report.isEnabled) return 'red';
        if (report.isSchedulable) return 'blue';
        if (report.isCronEnabled) return 'purple';
        return 'green';
    };

    /**
     * Get human-readable status text for report state display
     * Converts boolean states to descriptive text labels
     * Provides clear status communication to users
     */
    const getStatusText = (report) => {
        if (!report.isEnabled) return 'Disabled';
        if (report.isCronEnabled) return 'Scheduled';
        if (report.isSchedulable) return 'Schedulable';
        return 'Active';
    };

    const getTeamsAlertStatus =(report) =>{
        if (!report.isTeamsAlertEnabled) return 'Disabled';
        return 'Active';
    }

    const getTeamsAlertStatusColor = (report) =>{
        if (!report.isTeamsAlertEnabled) return 'red';
        return 'green';
    }

    const getReportTypeColor = (report) => {
        if(report.reportType === "critical") return 'red';      
        if(report.reportType === "issue") return 'orange';      
        if(report.reportType === "warning") return 'yellow';    
        if(report.reportType === "info") return 'blue';        
        return 'blue'
    }
    /**
     * Calculate optimal rows for textarea based on content
     * Auto-fits content height while maintaining reasonable limits
     * Provides better viewing experience without unnecessary scrolling
     */
    const calculateOptimalRows = (content, minRows = 3, maxRows = 25) => {
        if (!content) return minRows;
        
        // Count actual line breaks in content
        const lineCount = content.split('\n').length;
        
        // Add buffer for better readability
        const calculatedRows = Math.max(lineCount + 1, minRows);
        
        // Ensure within reasonable limits
        return Math.min(calculatedRows, maxRows);
    };

    /**
     * Calculate optimal rows for JSON content with formatting consideration
     * Takes into account JSON structure depth and complexity
     */
    const calculateJSONRows = (jsonContent, minRows = 4, maxRows = 20) => {
        if (!jsonContent) return minRows;
        
        try {
            // Parse and format JSON to get actual line count
            const parsed = JSON.parse(jsonContent);
            const formatted = JSON.stringify(parsed, null, 2);
            return calculateOptimalRows(formatted, minRows, maxRows);
        } catch (error) {
            // If JSON is invalid, calculate based on raw content
            return calculateOptimalRows(jsonContent, minRows, maxRows);
        }
    };

    /**
     * Render loading state with consistent styling and messaging
     * Provides visual feedback during data loading operations
     * Maintains UI consistency across loading states
     */
    const renderLoadingState = () => (
        <Flex justify="center" p={8}>
            <Spinner size="lg" color="brand.500" />
            <Text ml={3}>Loading reports...</Text>
        </Flex>
    );

    /**
     * Render empty state with call-to-action for new users
     * Provides guidance when no reports are available
     * Encourages user engagement with clear next steps
     */
    const renderEmptyState = () => (
        <Flex justify="center" p={8} direction="column" align="center">
            <Text fontSize="lg" color={textColor} mb={4}>
                No reports found
            </Text>
            <Button leftIcon={<MdAdd />} colorScheme="brand" onClick={openCreateModal}>
                Create Your First Report
            </Button>
        </Flex>
    );

    /**
     * Render comprehensive statistics dashboard with visual metrics
     * Displays key performance indicators and usage statistics
     * Provides quick insights into report system health and usage
     */
    const renderStatistics = () => {
        if (!statistics) return null;

        return (
            <SimpleGrid columns={{ base: 2, md: 4, lg: 6 }} spacing={4} mb={6}>

                <Card bg={cardBg} borderColor={borderColor}>
                    <CardBody>
                        <Stat>
                            <StatLabel>Total Reports</StatLabel>
                            <StatNumber>{formatNumber(statistics.totalReports || 0)}</StatNumber>
                        </Stat>
                    </CardBody>
                </Card>
                <Card bg={cardBg} borderColor={borderColor}>
                    <CardBody>
                        <Stat>
                            <StatLabel>Enabled</StatLabel>
                            <StatNumber color="green.500">{formatNumber(statistics.enabledReports || 0)}</StatNumber>
                        </Stat>
                    </CardBody>
                </Card>
                <Card bg={cardBg} borderColor={borderColor}>
                    <CardBody>
                        <Stat>
                            <StatLabel>Disabled</StatLabel>
                            <StatNumber color="red.500">{formatNumber(statistics.disabledReports || 0)}</StatNumber>
                        </Stat>
                    </CardBody>
                </Card>
                <Card bg={cardBg} borderColor={borderColor}>
                    <CardBody>
                        <Stat>
                            <StatLabel>Scheduled</StatLabel>
                            <StatNumber color="blue.500">{formatNumber(statistics.scheduledReports || 0)}</StatNumber>
                        </Stat>
                    </CardBody>
                </Card>
                <Card bg={cardBg} borderColor={borderColor}>
                    <CardBody>
                        <Stat>
                            <StatLabel>With Parameters</StatLabel>
                            <StatNumber>{formatNumber(statistics.reportsWithParameters || 0)}</StatNumber>
                        </Stat>
                    </CardBody>
                </Card>
                <Card bg={cardBg} borderColor={borderColor}>
                    <CardBody>
                        <Stat>
                            <StatLabel>Unmapped</StatLabel>
                            <StatNumber color="orange.500">{formatNumber(statistics.unmappedReports || 0)}</StatNumber>
                        </Stat>
                    </CardBody>
                </Card>
            </SimpleGrid>
        );
    };

    return (
        <Box>
            <VStack spacing={6} align="stretch">
                {/* Enhanced Page Header with Auto-Highlighting Context */}
                <Flex justify="space-between" align="center">
                    <Box>
                        <Heading size="lg" color={headingColor}>
                            Report Management
                        </Heading>
                        <Text color={textColor} mt={1}>
                            Manage SQL report definitions
                        </Text>
                    </Box>
                    <HStack spacing={3}>
                        <Button
                            leftIcon={<MdBarChart />}
                            onClick={onStatsOpen}
                            variant="outline"
                            size="sm"
                        >
                            Statistics
                        </Button>
                        <Button
                            leftIcon={<MdFileUpload />}
                            onClick={onImportOpen}
                            variant="outline"
                            size="sm"
                        >
                            Import
                        </Button>
                        <Tooltip label="Export complete report database to Excel (.xlsx) - All pages included" hasArrow placement="bottom">
                            <Button
                                leftIcon={<MdDownload />}
                                onClick={handleExportReportsExcel}
                                variant="outline"
                                size="sm"
                                isLoading={exportLoading}
                                loadingText="Exporting..."
                                isDisabled={pagination.total === 0}
                            >
                                Export All Data
                            </Button>
                        </Tooltip>
                        <Button
                            leftIcon={<MdRefresh />}
                            onClick={loadReports}
                            variant="outline"
                            size="sm"
                        >
                            Refresh
                        </Button>
                        <Button
                            leftIcon={<MdAdd />}
                            onClick={openCreateModal}
                            colorScheme="brand"
                            size="sm"
                        >
                            Create Report
                        </Button>
                    </HStack>
                </Flex>

                {/* Comprehensive Statistics Cards Display */}
                {renderStatistics()}

                {/* Enhanced Filters and Search with Extended Options */}
                <Card bg={cardBg} borderColor={borderColor}>
                    <CardBody>
                        <Flex wrap="wrap" gap={4} align="center" justify="space-between">
                            <HStack spacing={4} wrap="wrap" flex="1">
                                <InputGroup maxW="300px">
                                    <InputLeftElement>
                                        <MdSearch color={textColor} />
                                    </InputLeftElement>
                                    <Input
                                        placeholder="Search reports..."
                                        value={searchTerm}
                                        onChange={(e) => handleSearchChange(e.target.value)}
                                    />
                                </InputGroup>
                                
                                <Select
                                    value={statusFilter}
                                    onChange={(e) => handleStatusFilterChange(e.target.value)}
                                    maxW="200px"
                                >
                                    <option value="all">All Reports</option>
                                    <option value="enabled">Enabled</option>
                                    <option value="disabled">Disabled</option>
                                    <option value="scheduled">Scheduled</option>
                                    <option value="unscheduled">Unscheduled</option>
                                    <option value="schedulable">Schedulable</option>
                                    <option value="with_parameters">With Parameters</option>
                                    <option value="no_parameters">No Parameters</option>
                                </Select>

                                <Select
                                    value={pagination.size}
                                    onChange={(e) => handlePageSizeChange(e.target.value)}
                                    maxW="120px"
                                    size="sm"
                                >
                                    <option value={10}>10 per page</option>
                                    <option value={20}>20 per page</option>
                                    <option value={50}>50 per page</option>
                                    <option value={100}>100 per page</option>
                                </Select>
                            </HStack>

                            {selectedReports.length > 0 && (
                                <Button
                                    leftIcon={<MdDelete />}
                                    onClick={onBatchDeleteOpen}
                                    colorScheme="red"
                                    variant="outline"
                                    size="sm"
                                >
                                    Delete Selected ({selectedReports.length})
                                </Button>
                            )}
                        </Flex>
                    </CardBody>
                </Card>

                {/* Enhanced Reports Table with Comprehensive Data Display */}
                <Card bg={cardBg} borderColor={borderColor}>
                    <CardHeader>
                        <HStack justify="space-between" wrap="wrap">
                            <Heading size="md">
                                Reports ({formatNumber(pagination.total)})
                            </Heading>
                            <HStack spacing={4}>
                                <Badge colorScheme="blue" variant="subtle">
                                    Page {pagination.page} of {pagination.totalPages || 1}
                                </Badge>
                                <Badge colorScheme="green" variant="subtle">
                                    Showing {filteredReports.length} of {formatNumber(pagination.total)}
                                </Badge>
                            </HStack>
                        </HStack>
                    </CardHeader>
                    <CardBody p={0}>
                        {loading ? (
                            renderLoadingState()
                        ) : filteredReports.length === 0 ? (
                            renderEmptyState()
                        ) : (
                            <>
                                <TableContainer>
                                    <Table variant="simple" size="sm">
                                        <Thead>
                                            <Tr>
                                                <Th w="50px">
                                                    <Checkbox
                                                        isChecked={selectedReports.length === filteredReports.length && filteredReports.length > 0}
                                                        isIndeterminate={selectedReports.length > 0 && selectedReports.length < filteredReports.length}
                                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                                    />
                                                </Th>
                                                <Th>Code</Th>
                                                <Th>Description</Th>
                                                <Th>Alert Message</Th>
                                                <Th>Status</Th>
                                                <Th>Scheduling</Th>
                                                <Th>Parameters</Th>
                                                <Th>Created</Th>
                                                <Th w="120px">Actions</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {filteredReports.map((report) => (
                                                <Tr key={report.reportCode}>
                                                    <Td>
                                                        <Checkbox
                                                            isChecked={selectedReports.includes(report.reportCode)}
                                                            onChange={(e) => handleReportSelection(report.reportCode, e.target.checked)}
                                                        />
                                                    </Td>
                                                    <Td fontWeight="medium">
                                                        <Code colorScheme="blue" fontSize="sm">
                                                            {report.reportCode}
                                                        </Code>
                                                    </Td>
                                                    <Td>
                                                        <Tooltip label={report.reportDescription} hasArrow>
                                                            <Text
                                                                noOfLines={2}
                                                                maxW="250px"
                                                                fontSize="sm"
                                                            >
                                                                {report.reportDescription}
                                                            </Text>
                                                        </Tooltip>
                                                    </Td>
                                                    <Td>
                                                        <Tooltip label={report.alertMessage} hasArrow>
                                                            <Text
                                                                noOfLines={2}
                                                                maxW="200px"
                                                                fontSize="sm"
                                                            >
                                                                {report.alertMessage || 'No alert message'}
                                                            </Text>
                                                        </Tooltip>
                                                    </Td>
                                                    <Td>
                                                        <HStack spacing={2}>
                                                            <Switch
                                                                size="sm"
                                                                isChecked={report.isEnabled}
                                                                onChange={() => handleToggleStatus(report.reportCode, report.isEnabled)}
                                                            />
                                                            <Badge
                                                                colorScheme={getStatusColor(report)}
                                                                variant="subtle"
                                                            >
                                                                {getStatusText(report)}
                                                            </Badge>
                                                        </HStack>
                                                    </Td>
                                                    <Td>
                                                        <HStack spacing={2}>
                                                            <Switch
                                                                size="sm"
                                                                isChecked={report.isCronEnabled}
                                                                onChange={() => handleToggleScheduling(report.reportCode, report.isCronEnabled)}
                                                                isDisabled={!report.isEnabled}
                                                            />
                                                            <Badge
                                                                colorScheme={report.isCronEnabled ? 'blue' : 'gray'}
                                                                variant="subtle"
                                                            >
                                                                {report.isCronEnabled ? 'Active' : 'Inactive'}
                                                            </Badge>
                                                        </HStack>
                                                    </Td>
                                                    <Td>
                                                        <Badge
                                                            colorScheme={report.parameterCount > 0 ? 'green' : 'gray'}
                                                            variant="subtle"
                                                        >
                                                            {report.parameterCount || 0}
                                                        </Badge>
                                                    </Td>
                                                    <Td fontSize="sm" color={textColor}>
                                                        {formatDateTime(report.createdAtUtc)}
                                                    </Td>
                                                    <Td>
                                                        <Menu>
                                                            <MenuButton
                                                                as={IconButton}
                                                                icon={<MdMoreVert />}
                                                                variant="ghost"
                                                                size="sm"
                                                            />
                                                            <MenuList>
                                                                <MenuItem
                                                                    icon={<MdVisibility />}
                                                                    onClick={() => openViewModal(report)}
                                                                >
                                                                    View Details
                                                                </MenuItem>
                                                                <MenuItem
                                                                    icon={<MdEdit />}
                                                                    onClick={() => openEditModal(report)}
                                                                >
                                                                    Edit
                                                                </MenuItem>
                                                                <MenuItem
                                                                    icon={<MdFileDownload />}
                                                                    onClick={() => handleExportReport(report.reportCode)}
                                                                >
                                                                    Export
                                                                </MenuItem>
                                                                <Divider />
                                                                <MenuItem
                                                                    icon={<MdDelete />}
                                                                    onClick={() => openDeleteModal(report)}
                                                                    color="red.500"
                                                                >
                                                                    Delete
                                                                </MenuItem>
                                                            </MenuList>
                                                        </Menu>
                                                    </Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </TableContainer>

                                {/* Enhanced Pagination Controls */}
                                {pagination.totalPages > 1 && (
                                    <Flex justify="space-between" align="center" p={4} borderTop="1px" borderTopColor={borderColor}>
                                        <Text fontSize="sm" color={textColor}>
                                            Showing {((pagination.page - 1) * pagination.size) + 1} to {Math.min(pagination.page * pagination.size, pagination.total)} of {pagination.total} entries
                                        </Text>
                                        
                                        <HStack spacing={2}>
                                            <Button
                                                size="sm"
                                                onClick={() => handlePageChange(1)}
                                                isDisabled={!pagination.hasPrevious}
                                                variant="outline"
                                            >
                                                First
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={() => handlePageChange(pagination.page - 1)}
                                                isDisabled={!pagination.hasPrevious}
                                                variant="outline"
                                            >
                                                Previous
                                            </Button>
                                            
                                            {/* Page Numbers */}
                                            {(() => {
                                                const pages = [];
                                                const maxVisible = 5;
                                                let startPage = Math.max(1, pagination.page - 2);
                                                let endPage = Math.min(pagination.totalPages, startPage + maxVisible - 1);
                                                
                                                if (endPage - startPage < maxVisible - 1) {
                                                    startPage = Math.max(1, endPage - maxVisible + 1);
                                                }
                                                
                                                for (let i = startPage; i <= endPage; i++) {
                                                    pages.push(
                                                        <Button
                                                            key={i}
                                                            size="sm"
                                                            variant={i === pagination.page ? "solid" : "outline"}
                                                            colorScheme={i === pagination.page ? "brand" : "gray"}
                                                            onClick={() => handlePageChange(i)}
                                                        >
                                                            {i}
                                                        </Button>
                                                    );
                                                }
                                                return pages;
                                            })()}
                                            
                                            <Button
                                                size="sm"
                                                onClick={() => handlePageChange(pagination.page + 1)}
                                                isDisabled={!pagination.hasNext}
                                                variant="outline"
                                            >
                                                Next
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={() => handlePageChange(pagination.totalPages)}
                                                isDisabled={!pagination.hasNext}
                                                variant="outline"
                                            >
                                                Last
                                            </Button>
                                        </HStack>
                                    </Flex>
                                )}
                            </>
                        )}
                    </CardBody>
                </Card>

                {/* Enhanced Create/Edit Report Modal WITHOUT JSON Highlighting */}
                <Modal 
                    isOpen={isCreateOpen || isEditOpen} 
                    onClose={isCreateOpen ? onCreateClose : onEditClose} 
                    size="6xl"
                >
                    <ModalOverlay />
                    <ModalContent maxH="90vh">
                        <ModalHeader>
                            {isCreateOpen ? 'Create New Report' : 'Edit Report'}
                        </ModalHeader>
                        <ModalCloseButton />
                        <ModalBody overflowY="auto">
                            <Tabs>
                                <TabList>
                                    <Tab>Basic Information</Tab>
                                    <Tab>SQL Report Query</Tab>
                                    <Tab>SQL Data Query</Tab>
                                    <Tab>Parameters</Tab>
                                    <Tab>Scheduling</Tab>
                                </TabList>

                                <TabPanels>
                                    <TabPanel>
                                        <VStack spacing={4}>
                                            <FormControl isRequired isInvalid={!!formErrors.reportDescription}>
                                                <FormLabel>Report Description</FormLabel>
                                                <Textarea
                                                    value={formData.reportDescription}
                                                    onChange={(e) => {
                                                        setFormData(prev => ({ ...prev, reportDescription: e.target.value }));
                                                        if (formErrors.reportDescription) {
                                                            setFormErrors(prev => ({ ...prev, reportDescription: undefined }));
                                                        }
                                                    }}
                                                    placeholder="Enter descriptive report name and purpose"
                                                    rows={3}
                                                    maxLength={1000}
                                                />
                                                <FormErrorMessage>{formErrors.reportDescription}</FormErrorMessage>
                                                <Text fontSize="xs" color={textColor} mt={1}>
                                                    {formData.reportDescription.length}/1000 characters
                                                </Text>
                                            </FormControl>
                                            
                                            <FormControl isRequired isInvalid={!!formErrors.alertMessage}>
                                                <FormLabel>Report Alert Message</FormLabel>
                                                <Textarea
                                                    value={formData.alertMessage}
                                                    onChange={(e) => handleAlertMessageChange(e.target.value)}
                                                    placeholder="Enter alert message to be shown when report meet the condition"
                                                    rows={3}
                                                    maxLength={2000}
                                                />
                                                <FormErrorMessage>{formErrors.alertMessage}</FormErrorMessage>
                                                <Text fontSize="xs" color={textColor} mt={1}>
                                                    {formData.alertMessage.length}/2000 characters. This message will be displayed in system notifications.
                                                </Text>
                                            </FormControl>
                                            <FormControl>
                                                <FormLabel>Status</FormLabel>
                                                <HStack>
                                                    <Switch
                                                        isChecked={formData.isEnabled}
                                                        onChange={(e) => setFormData(prev => ({ ...prev, isEnabled: e.target.checked }))}
                                                    />
                                                    <Text fontSize="sm">
                                                        {formData.isEnabled ? 'Enabled' : 'Disabled'}
                                                    </Text>
                                                </HStack>
                                            </FormControl>
                                            <FormControl>
                                                <FormLabel>Send Alerts to Teams Channel</FormLabel>
                                                <HStack>
                                                    <Switch
                                                        isChecked={formData.isTeamsAlertEnabled}
                                                        onChange={(e) => setFormData(prev => ({ ...prev, isTeamsAlertEnabled: e.target.checked }))}
                                                    />
                                                    <Text fontSize="sm">
                                                        {formData.isTeamsAlertEnabled ? 'Enabled' : 'Disabled'}
                                                    </Text>
                                                </HStack>
                                            </FormControl>
                                            <FormControl>
                                                <FormLabel>Auto Close Issues for this Report</FormLabel>
                                                <HStack>
                                                    <Switch
                                                        isChecked={formData.isAutoCloseIssues}
                                                        onChange={(e) => setFormData(prev => ({ ...prev, isAutoCloseIssues: e.target.checked }))}
                                                    />
                                                    <Text fontSize="sm">
                                                        {formData.isAutoCloseIssues ? 'Enabled' : 'Disabled'}
                                                    </Text>
                                                </HStack>
                                            </FormControl>
                                            {/* <FormControl>
                                                <FormLabel>Is Critical Report</FormLabel>
                                                <HStack>
                                                    <Switch
                                                        isChecked={formData.isCriticalReport}
                                                        onChange={(e) => setFormData(prev => ({ ...prev, isCriticalReport: e.target.checked }))}
                                                    />
                                                    <Text fontSize="sm">
                                                        {formData.isCriticalReport ? 'Critical' : 'Normal'}
                                                    </Text>
                                                </HStack>
                                            </FormControl> */}
                                            <FormControl>
                                                <FormLabel>Repeat Alert if Issue Stays Open</FormLabel>
                                                <HStack>
                                                    <Switch
                                                        isChecked={formData.alertRepeatEnabled}
                                                        onChange={(e) => setFormData(prev => ({ ...prev, alertRepeatEnabled: e.target.checked }))}
                                                    />
                                                    <Text fontSize="sm">
                                                        {formData.alertRepeatEnabled ? 'Enabled' : 'Disabled'}
                                                    </Text>
                                                </HStack>
                                                <Text fontSize="xs" color={textColor} mt={1}>
                                                    When enabled, alerts will be re-sent repeatedly until the issue is resolved or closed.
                                                </Text>
                                            </FormControl>
                                            <FormControl>
                                                <FormLabel>Report Type</FormLabel>
                                                <Select
                                                    value={formData.reportType || 'info'}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, reportType: e.target.value }))}
                                                    placeholder="Select report type"
                                                >
                                                    <option value="critical">Critical</option>
                                                    <option value="issue">Issue</option>
                                                    <option value="warning">Warning</option>
                                                    <option value="info">Info</option>
                                                </Select>
                                            </FormControl>
                                            <FormControl>
                                                <FormLabel>Report Module</FormLabel>
                                                <HStack>
                                                    <Select
                                                        value={formData.reportModule || ''}
                                                        onChange={(e) => setFormData(prev => ({ ...prev, reportModule: e.target.value }))}
                                                        placeholder="Select report module"
                                                    >
                                                        {reportModules.map(m => (
                                                            <option key={m.id ?? m.value} value={m.value}>{m.name}</option>
                                                        ))}
                                                    </Select>
                                                    <Tooltip label="Add new module" hasArrow>
                                                        <IconButton
                                                            aria-label="Add new module"
                                                            icon={<MdAdd />}
                                                            onClick={() => { setNewModuleName(''); onModuleOpen(); }}
                                                            variant="outline"
                                                            flexShrink={0}
                                                        />
                                                    </Tooltip>
                                                </HStack>
                                            </FormControl>
                                        </VStack>
                                    </TabPanel>

                                    <TabPanel>
                                        <FormControl isRequired isInvalid={!!formErrors.report}>
                                            <FormLabel>SQL Report  Query</FormLabel>
                                            <SQLHighlighter
                                                value={formData.report}
                                                onChange={handleSQLChange}
                                                placeholder="SELECT COUNT(*) as countValue FROM table_name WITH(NOLOCK) WHERE condition..."
                                                rows={15}
                                                maxLength={10000}
                                                bg={codeBg}
                                                showHighlightButton={true}
                                                autoHighlight={false}
                                            />
                                            <FormErrorMessage>{formErrors.report}</FormErrorMessage>
                                            <Text fontSize="xs" color={textColor} mt={2}>
                                                {formData.report.length}/10000 characters. Query must start with SELECT and use WITH(NOLOCK) for performance.
                                                Click "Highlight SQL" to apply syntax highlighting to keywords.
                                            </Text>
                                        </FormControl>
                                    </TabPanel>
                                    <TabPanel>
                                        <FormControl isRequired isInvalid={!!formErrors.dataQuery}>
                                            <FormLabel>SQL Data Query</FormLabel>
                                            <SQLHighlighter
                                                value={formData.dataQuery}
                                                onChange={handleDataSQLChange}
                                                placeholder="SELECT column1, column2 FROM table_name WITH(NOLOCK) WHERE condition..."
                                                rows={15}
                                                maxLength={10000}
                                                bg={codeBg}
                                                showHighlightButton={true}
                                                autoHighlight={false}
                                            />
                                            <FormErrorMessage>{formErrors.dataQuery}</FormErrorMessage>
                                            <Text fontSize="xs" color={textColor} mt={2}>
                                                {formData.dataQuery.length}/10000 characters. Query must start with SELECT and use WITH(NOLOCK) for performance.
                                                Click "Highlight SQL" to apply syntax highlighting to keywords.
                                            </Text>
                                        </FormControl>
                                    </TabPanel>  

                                    <TabPanel>
                                        <FormControl isInvalid={!!formErrors.parametersJson}>
                                            <FormLabel>Parameters (JSON Formatting Only)</FormLabel>
                                            <JSONFormatter
                                                value={formData.parametersJson}
                                                onChange={handleParametersChange}
                                                placeholder='[{"category": "directValue", "index": 1, "type": "string", "value": "", "name": "Client Name", "required": true}]'
                                                rows={12}
                                                bg={codeBg}
                                                showValidation={true}
                                                showFormatButton={true}
                                                showHighlightButton={false}
                                                autoHighlight={false}
                                            />
                                            <FormErrorMessage>{formErrors.parametersJson}</FormErrorMessage>
                                            <Text fontSize="xs" color={textColor} mt={2}>
                                                Define parameters for dynamic query execution. Click "Format JSON" to format and validate JSON structure.
                                                Each parameter must have category, index, type, and name fields. Only formatting available in edit mode.
                                            </Text>
                                        </FormControl>
                                    </TabPanel>

                                    <TabPanel>
                                        <VStack spacing={4}>
                                            <FormControl>
                                                <FormLabel>Enable Scheduled Execution</FormLabel>
                                                <HStack>
                                                    <Switch
                                                        isChecked={formData.isCronEnabled}
                                                        onChange={(e) => setFormData(prev => ({ ...prev, isCronEnabled: e.target.checked }))}
                                                    />
                                                    <Text fontSize="sm">
                                                        {formData.isCronEnabled ? 'Enabled' : 'Disabled'}
                                                    </Text>
                                                </HStack>
                                            </FormControl>
                                            
                                            {formData.isCronEnabled && (
                                                <FormControl isRequired isInvalid={!!formErrors.cronExpression}>
                                                    <HStack spacing={1} mb={2}>
                                                        <FormLabel mb={0}>Cron Expression</FormLabel>
                                                        <Popover placement="right" isLazy>
                                                            <PopoverTrigger>
                                                                <IconButton
                                                                    aria-label="Cron expression examples"
                                                                    icon={<MdInfoOutline />}
                                                                    size="xs"
                                                                    variant="ghost"
                                                                />
                                                            </PopoverTrigger>
                                                            <PopoverContent w="300px">
                                                                <PopoverArrow />
                                                                <PopoverCloseButton />
                                                                <PopoverHeader fontWeight="semibold" fontSize="sm">
                                                                    Common cron values
                                                                </PopoverHeader>
                                                                <PopoverBody>
                                                                    <VStack align="stretch" spacing={2}>
                                                                        {CRON_EXAMPLES.map(ex => (
                                                                            <HStack key={ex.value} justify="space-between">
                                                                                <Text fontSize="sm">{ex.label}</Text>
                                                                                <Tooltip label="Use this value" hasArrow>
                                                                                    <Code
                                                                                        fontSize="xs"
                                                                                        cursor="pointer"
                                                                                        onClick={() => {
                                                                                            setFormData(prev => ({ ...prev, cronExpression: ex.value }));
                                                                                            if (formErrors.cronExpression) {
                                                                                                setFormErrors(prev => ({ ...prev, cronExpression: undefined }));
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        {ex.value}
                                                                                    </Code>
                                                                                </Tooltip>
                                                                            </HStack>
                                                                        ))}
                                                                    </VStack>
                                                                    <Text fontSize="xs" color={textColor} mt={3}>
                                                                        Click a value to use it. Format: second minute hour day month dayOfWeek (UTC).
                                                                    </Text>
                                                                </PopoverBody>
                                                            </PopoverContent>
                                                        </Popover>
                                                    </HStack>
                                                    <Input
                                                        value={formData.cronExpression}
                                                        onChange={(e) => {
                                                            setFormData(prev => ({ ...prev, cronExpression: e.target.value }));
                                                            if (formErrors.cronExpression) {
                                                                setFormErrors(prev => ({ ...prev, cronExpression: undefined }));
                                                            }
                                                        }}
                                                        placeholder="0 */30 * * * ?"
                                                        fontFamily="monospace"
                                                    />
                                                    <FormErrorMessage>{formErrors.cronExpression}</FormErrorMessage>
                                                    <Text fontSize="xs" color={textColor} mt={2}>
                                                        Standard cron expression format (6 parts: second minute hour day month dayOfWeek).
                                                        Example: "0 0 8 * * ?" runs daily at 8 AM
                                                    </Text>
                                                </FormControl>
                                            )}
                                        </VStack>
                                    </TabPanel>
                                </TabPanels>
                            </Tabs>
                        </ModalBody>
                        <ModalFooter>
                            <Button 
                                variant="ghost" 
                                mr={3} 
                                onClick={isCreateOpen ? onCreateClose : onEditClose}
                            >
                                Cancel
                            </Button>
                            <Button 
                                colorScheme="brand" 
                                onClick={isCreateOpen ? handleCreateReport : handleUpdateReport}
                            >
                                {isCreateOpen ? 'Create Report' : 'Update Report'}
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* Enhanced View Report Modal with Auto-Highlighting Enabled */}
                <Modal isOpen={isViewOpen} onClose={onViewClose} size="6xl">
                    <ModalOverlay />
                    <ModalContent maxH="90vh">
                        <ModalHeader>Report Details: {currentReport?.reportCode}</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody overflowY="auto">
                            {currentReport && (
                                <VStack spacing={6} align="stretch">
                                    <SimpleGrid columns={4} spacing={3}>
                                        <Box>
                                            <Text fontWeight="bold" mb={2}>Report Code:</Text>
                                            <Code colorScheme="blue">{currentReport.reportCode}</Code>
                                        </Box>
                                        <Box>
                                            <Text fontWeight="bold" mb={2}>Status:</Text>
                                            <Badge colorScheme={getStatusColor(currentReport)}>
                                                {getStatusText(currentReport)}
                                            </Badge>
                                        </Box>
                                        <Box>
                                            <Text fontWeight="bold" mb={2}>Teams Alert Status:</Text>
                                            <Badge colorScheme={getTeamsAlertStatusColor(currentReport)}>
                                                {getTeamsAlertStatus(currentReport)}
                                            </Badge>
                                        </Box>
                                        <Box>
                                            <Text fontWeight="bold" mb={2}>Repeat Alert Policy:</Text>
                                            <Badge colorScheme={currentReport.alertRepeatEnabled ? 'orange' : 'gray'}>
                                                {currentReport.alertRepeatEnabled ? 'Repeating until resolved' : 'No Repeat'}
                                            </Badge>
                                        </Box>
                                        <Box>
                                            <Text fontWeight="bold" mb={2}>Report Type:</Text>
                                            <Badge colorScheme={getReportTypeColor(currentReport)}>
                                                {currentReport.reportType}
                                            </Badge>
                                        </Box>
                                    </SimpleGrid>
                                    
                                    <Box>
                                        <Text fontWeight="bold" mb={2}>Description:</Text>
                                        <Text fontSize="sm" color={textColor}>
                                            {currentReport.reportDescription}
                                        </Text>
                                    </Box>
                                    
                                    <Box>
                                        <Text fontWeight="bold" mb={2}>Alert Message:</Text>
                                        <Text fontSize="sm" color={textColor} p={3} bg={codeBg} borderRadius="md">
                                            {currentReport.alertMessage || 'No alert message configured'}
                                        </Text>
                                    </Box>
                                    
                                    <Box>
                                        <Flex justify="space-between" align="center" mb={2}>
                                            <Text fontWeight="bold">SQL Query :</Text>
                                            <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                                                Auto-sized to fit content
                                            </Badge>
                                        </Flex>
                                        <SQLHighlighter
                                            value={currentReport.report}
                                            onChange={() => {}} // Read-only in view mode
                                            isDisabled={true}
                                            rows={calculateOptimalRows(currentReport.report, 8, 30)}
                                            bg={codeBg}
                                            showHighlightButton={false}
                                            autoHighlight={true}
                                            autoSize={true}
                                            minRows={8}
                                            maxRows={30}
                                        />
                                    </Box>
                                    
                                    <Box>
                                        <Flex justify="space-between" align="center" mb={2}>
                                            <Text fontWeight="bold">Parameters ({currentReport.parameterCount}) :</Text>
                                            <Badge colorScheme="green" variant="subtle" fontSize="xs">
                                                View Mode - Auto-highlighted
                                            </Badge>
                                        </Flex>
                                        <JSONFormatter
                                            value={JSON.stringify(currentReport.parameters || [], null, 2)}
                                            onChange={() => {}} // Read-only in view mode
                                            isDisabled={true}
                                            rows={calculateJSONRows(JSON.stringify(currentReport.parameters || [], null, 2), 4, 25)}
                                            bg={codeBg}
                                            showValidation={false}
                                            showFormatButton={false}
                                            showHighlightButton={false}
                                            autoHighlight={true}
                                            autoSize={true}
                                            minRows={4}
                                            maxRows={25}
                                        />
                                    </Box>

                                    {currentReport.isCronEnabled && (
                                        <Box>
                                            <Text fontWeight="bold" mb={2}>Cron Expression:</Text>
                                            <Code>{currentReport.cronExpression}</Code>
                                        </Box>
                                    )}

                                    <SimpleGrid columns={2} spacing={4}>
                                        <Box>
                                            <Text fontWeight="bold" mb={2}>Created:</Text>
                                            <Text fontSize="sm">{formatDateTime(currentReport.createdAtUtc)}</Text>
                                            <Text fontSize="xs" color={textColor}>by {currentReport.createdBy}</Text>
                                        </Box>
                                        {currentReport.updatedAtUtc && (
                                            <Box>
                                                <Text fontWeight="bold" mb={2}>Last Updated:</Text>
                                                <Text fontSize="sm">{formatDateTime(currentReport.updatedAtUtc)}</Text>
                                                <Text fontSize="xs" color={textColor}>by {currentReport.updatedBy}</Text>
                                            </Box>
                                        )}
                                    </SimpleGrid>

                                </VStack>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button onClick={onViewClose}>Close</Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* Enhanced Import Report Modal with Auto-Formatting JSON */}
                <Modal isOpen={isImportOpen} onClose={onImportClose} size="xl">
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Import Report Definition</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <FormControl>
                                <FormLabel>Report Definition JSON with Auto-Highlighting & Formatting</FormLabel>
                                <JSONFormatter
                                    value={importData}
                                    onChange={handleImportDataChange}
                                    placeholder="Paste exported report JSON here..."
                                    rows={12}
                                    showValidation={true}
                                    showFormatButton={true}
                                    showHighlightButton={false}
                                    autoHighlight={false}
                                />
                                <Text fontSize="xs" color={textColor} mt={2}>
                                    Paste exported report JSON data. Click "Format JSON" to validate and format structure.
                                    Must include report, reportDescription, and alertMessage fields.
                                </Text>
                            </FormControl>
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="ghost" mr={3} onClick={onImportClose}>
                                Cancel
                            </Button>
                            <Button colorScheme="brand" onClick={handleImportReport}>
                                Import Report
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* Comprehensive Statistics Modal with Detailed Metrics */}
                <Modal isOpen={isStatsOpen} onClose={onStatsClose} size="2xl">
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Comprehensive Report Statistics</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            {statistics && (
                                <SimpleGrid columns={2} spacing={4}>
                                    <Stat>
                                        <StatLabel>Total Reports</StatLabel>
                                        <StatNumber>{formatNumber(statistics.totalReports || 0)}</StatNumber>
                                    </Stat>
                                    <Stat>
                                        <StatLabel>Enabled Reports</StatLabel>
                                        <StatNumber color="green.500">{formatNumber(statistics.enabledReports || 0)}</StatNumber>
                                    </Stat>
                                    <Stat>
                                        <StatLabel>Disabled Reports</StatLabel>
                                        <StatNumber color="red.500">{formatNumber(statistics.disabledReports || 0)}</StatNumber>
                                    </Stat>
                                    <Stat>
                                        <StatLabel>Scheduled Reports</StatLabel>
                                        <StatNumber color="blue.500">{formatNumber(statistics.scheduledReports || 0)}</StatNumber>
                                    </Stat>
                                    <Stat>
                                        <StatLabel>Reports with Parameters</StatLabel>
                                        <StatNumber>{formatNumber(statistics.reportsWithParameters || 0)}</StatNumber>
                                    </Stat>
                                    <Stat>
                                        <StatLabel>Unmapped Reports</StatLabel>
                                        <StatNumber color="orange.500">{formatNumber(statistics.unmappedReports || 0)}</StatNumber>
                                    </Stat>
                                    <Stat>
                                        <StatLabel>Average Parameters</StatLabel>
                                        <StatNumber>{statistics.averageParametersPerReport?.toFixed(1) || '0.0'}</StatNumber>
                                    </Stat>
                                    <Stat>
                                        <StatLabel>Most Common Schedule</StatLabel>
                                        <StatHelpText>
                                            <Code fontSize="xs">{statistics.mostCommonSchedule || 'N/A'}</Code>
                                        </StatHelpText>
                                    </Stat>
                                </SimpleGrid>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button onClick={onStatsClose}>Close</Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* Enhanced Delete Confirmation Modal with Warning Context */}
                <AlertDialog isOpen={isDeleteOpen} onClose={onDeleteClose}>
                    <AlertDialogOverlay>
                        <AlertDialogContent>
                            <AlertDialogHeader>Confirm Deletion</AlertDialogHeader>
                            <AlertDialogBody>
                                <Alert status="warning" borderRadius="md" mb={4}>
                                    <AlertIcon />
                                    Are you sure you want to delete report "{currentReport?.reportCode}"?
                                </Alert>
                                <Text fontSize="sm" color={textColor}>
                                    This action will permanently remove the report definition and all associated mappings. 
                                    This operation cannot be undone and will affect any scheduled executions.
                                </Text>
                            </AlertDialogBody>
                            <AlertDialogFooter>
                                <Button onClick={onDeleteClose}>Cancel</Button>
                                <Button colorScheme="red" onClick={handleDeleteReport} ml={3}>
                                    Delete Report
                                </Button>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialogOverlay>
                </AlertDialog>

                {/* Enhanced Batch Delete Confirmation Modal with Impact Details */}
                <AlertDialog isOpen={isBatchDeleteOpen} onClose={onBatchDeleteClose}>
                    <AlertDialogOverlay>
                        <AlertDialogContent>
                            <AlertDialogHeader>Confirm Batch Deletion</AlertDialogHeader>
                            <AlertDialogBody>
                                <Alert status="warning" borderRadius="md" mb={4}>
                                    <AlertIcon />
                                    Are you sure you want to delete {selectedReports.length} selected reports?
                                </Alert>
                                <Text fontSize="sm" color={textColor}>
                                    This action will permanently remove all selected report definitions and their 
                                    associated mappings. This operation cannot be undone and will stop any scheduled executions.
                                </Text>
                            </AlertDialogBody>
                            <AlertDialogFooter>
                                <Button onClick={onBatchDeleteClose}>Cancel</Button>
                                <Button colorScheme="red" onClick={handleBatchDelete} ml={3}>
                                    Delete {selectedReports.length} Reports
                                </Button>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialogOverlay>
                </AlertDialog>

                {/* Add Report Module modal */}
                <Modal isOpen={isModuleOpen} onClose={onModuleClose} isCentered>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Add Report Module</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <FormControl isRequired>
                                <FormLabel>Module Name</FormLabel>
                                <Input
                                    value={newModuleName}
                                    onChange={(e) => setNewModuleName(e.target.value)}
                                    placeholder="e.g. Bulk Mailer V3"
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleCreateModule(); }}
                                    autoFocus
                                />
                                <Text fontSize="xs" color={textColor} mt={1}>
                                    Module name must be unique.
                                </Text>
                            </FormControl>
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="ghost" mr={3} onClick={onModuleClose}>Cancel</Button>
                            <Button
                                colorScheme="brand"
                                onClick={handleCreateModule}
                                isLoading={moduleSaving}
                                loadingText="Saving..."
                            >
                                Create Module
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            </VStack>
        </Box>
    );
}

export default Report;